import { ComponentType, Dispatch, Key, ReactNode, SetStateAction } from 'react'
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form'
import { Node } from 'reactflow'

export type Class<T> = new () => T

export interface ControlledProps<T> {
    value: T
    onChange: (value: T) => void
}

export interface CommonInputProps {
    required?: boolean
    disabled?: boolean
    label?: ReactNode
    subtitle?: ReactNode
    hideLabel?: boolean
    error?: ReactNode
}

export type ControlledInputProps<T> = ControlledProps<T> & CommonInputProps

export interface FieldProps<X extends FieldValues, P extends FieldPath<X>> extends CommonInputProps {
    form: UseFormReturn<X>
    name: P
}

export type FieldBindingsProps<I extends ControlledInputProps<T>, T, X extends FieldValues, P extends FieldPath<X>> = Omit<I, keyof ControlledProps<T>> & FieldProps<X, P>

export interface OptionsProps<O, V = O> {
    options: O[] | readonly O[]
    toValue?: (option: O) => V
    getValueKey?: (option: V) => Key
    getOptionDisplay?: (option: O) => ReactNode
}

export type UseStateContext<T> = [T, Dispatch<SetStateAction<T>>]

export interface OAuthResponse {
    refresh_token: string
    access_token: string
    expires_at: Date
    refresh_expires_at: Date
}

export type Operator = '=' | '!=' | '<' | '<=' | '>' | '>=' | '=' | 'is set' | 'is not set' | 'or' | 'and' | 'xor' | 'empty' | 'contains' | 'not contain' | 'starts with' | 'not start with' | 'ends with' | 'any' | 'none' | 'is same day'
export type RuleType = 'wrapper' | 'string' | 'number' | 'boolean' | 'date' | 'array' | 'list' | 'version'
export type RuleGroup = 'user' | 'event' | 'parent' | 'root' | 'journey'

export type AnyJson = boolean | number | string | null | JsonArray | JsonMap
export interface JsonMap { [key: string]: AnyJson }
export type JsonArray = AnyJson[]

export type Rule = {
    uuid: string
    root_uuid?: string
    parent_uuid?: string
    type: RuleType
    group: RuleGroup
    path: string
    operator: Operator
    value?: string | string[]

    // Absent means exact case, matching how the server evaluates rules stored
    // before this flag existed
    case_sensitive?: boolean
} & (
    | { type: 'wrapper' }
    | { type: 'string' }
    | { type: 'number' }
    | { type: 'boolean' }
    | { type: 'date' }
    | { type: 'array' }
    | { type: 'list' }
    | { type: 'version' }
)

export type WrapperRule = Rule & { type: 'wrapper', children: Rule[] }

export type EventRulePeriod = {
    type: 'rolling'
    unit: 'minute' | 'hour' | 'day' | 'week' | 'month'
    value: number
} | {
    type: 'fixed'
    start_date: string
    end_date?: string
}

export interface EventRuleFrequency {
    period: EventRulePeriod
    operator: Operator
    count: number | undefined
}

export type EventRule = {
    group: 'event'
    frequency?: EventRuleFrequency
} & WrapperRule

export interface RulePath {
    id: number
    path: string
    type: 'user' | 'event'
    name: string
    data_type: 'string' | 'number' | 'boolean' | 'date' | 'array' | 'version'
    visibility: 'public' | 'hidden' | 'classified'
}

export interface AllowedEvent {
    id: number
    project_id: number
    name: string
    created_at: string
    updated_at: string
}

export interface VariableSuggestions {
    userPaths: RulePath[]
    eventPaths: {
        [name: string]: RulePath[]
    }
    journeyPaths?: RulePath[]
}

export interface Preferences {
    readonly lang: string
    readonly mode: 'light' | 'dark'
    readonly timeZone: string
}

export interface SearchParams {
    cursor?: string
    cursor_direction?: 'prev' | 'next'
    page?: number
    limit: number
    sort?: string
    direction?: string
    filter?: Record<string, any>
    q?: string
    id?: Array<number | string>
}

export interface SearchResult<T> {
    results: T[]
    nextCursor: string
    prevCursor?: string
    limit: number
}

type FolderType = 'list' | 'journey' | 'campaign' | 'folder'
interface FolderPath { id: number, name: string }
export interface Folder {
    id: number
    type: FolderType
    parent_id?: number
    name: string
    path: FolderPath[]
}

export type FolderEditParams = Partial<Folder> & Pick<Folder, 'parent_id' | 'type'>
export type FolderCreateParams = Omit<Folder, 'id' | 'path'>
export type FolderUpdateParams = Pick<Folder, 'id' | 'name'>

export interface FolderSearchResult<T> {
    folder: Folder
    folders: Folder[]
    folder_count: number
    results: T[]
    result_count: number
}

export type AuditFields = 'created_at' | 'updated_at' | 'deleted_at'

export interface AuthMethod {
    driver: string
    name: string
}

export interface AuthCheckResponse {
    hasSAML: boolean
    samlName?: string
}

export interface SAMLConfigResponse {
    configured: boolean
    entryPoint?: string
    issuer?: string
    cert?: string
    callbackUrl?: string
    wantAuthnResponseSigned?: boolean
    name?: string
    saml_auto_create?: boolean
}

export interface SAMLConfigParams {
    name?: string
    entryPoint: string
    issuer: string
    cert: string
    wantAuthnResponseSigned?: boolean
    saml_auto_create?: boolean
}

export const organizationRoles = [
    'member',
    'admin',
    'owner',
    'root',
] as const

export type OrganizationRole = (typeof organizationRoles)[number]

export interface Admin {
    id: number
    organization_id: number
    first_name: string
    last_name: string
    email: string
    image_url: string
    role: OrganizationRole
}

export type OrganizationType = 'default' | 'managed'

export interface Organization {
    id: number
    username: string
    domain?: string
    type: OrganizationType
    auth: any
    tracking_deeplink_mirror_url?: string
}

export type OrganizationUpdateParams = Omit<Organization, 'id' | 'type' | 'auth' | AuditFields>

export interface OrganizationCreateParams {
    username: string
    domain?: string
}

export const projectRoles = [
    'support',
    'editor',
    'publisher',
    'developer',
    'admin',
] as const

export type ProjectRole = (typeof projectRoles)[number]

export interface ProjectAdmin extends Omit<Admin, 'id' | 'role'> {
    id: number
    created_at: string
    updated_at: string
    project_id: number
    admin_id: number
    role: ProjectRole
}

export type ProjectAdminParams = Pick<ProjectAdmin, 'role'>
export type ProjectAdminInviteParams = ProjectAdminParams & {
    email: string
}

interface OptOut {
    first: Record<string, string>
    regular: Record<string, string>
}

export interface ExternalIdRule {
    type: 'string' | 'number' | 'uuid' | 'email'
    pattern?: string
    example?: string
}

export interface Project {
    id: number
    organization_id?: number
    name: string
    description?: string
    locale: string
    timezone: string
    text_opt_out?: OptOut
    text_help?: Record<string, string>
    link_wrap_email: boolean
    link_wrap_push: boolean
    link_wrap_in_app: boolean
    locale_override?: string
    logo_url?: string
    external_id_rule?: ExternalIdRule
    event_whitelist_enabled?: boolean
    allow_email_identification?: boolean
    created_at: string
    updated_at: string
    deleted_at?: string
    role?: ProjectRole
    has_provider?: boolean
}

export type ChannelType = 'email' | 'push' | 'text' | 'webhook' | 'in_app'

export type ProjectCreate = Omit<Project, 'id' | AuditFields>

export interface ProjectApiKey {
    id: number
    value?: string
    name: string
    scope: 'public' | 'secret'
    role?: ProjectRole
    description?: string
}

export type ProjectApiKeyParams = Pick<ProjectApiKey, 'name' | 'description' | 'scope' | 'role'>

export const webhookEvents = [
    'message.sent',
    'message.failed',
    'message.suppressed',
    'message.bounced',
    'message.complained',
    'message.delivered',
    'message.opened',
    'message.clicked',
    'suppression.created',
    'suppression.deleted',
] as const

export type WebhookEvent = (typeof webhookEvents)[number]

export interface WebhookSubscription {
    id: number
    url: string
    events?: WebhookEvent[] | null
    enabled: boolean
    created_at: string
    updated_at: string
}

export type WebhookSubscriptionParams = Pick<WebhookSubscription, 'url' | 'events' | 'enabled'>

export interface WebhookSubscriptionCreated extends WebhookSubscription {
    secret: string
}

export type WebhookDeliveryOutcome = 'success' | 'failed' | 'blocked' | 'deadletter'

export interface WebhookDelivery {
    id: string
    event: string
    event_id: string
    message_id?: string
    address?: string
    url: string
    attempt: number
    max_attempts: number
    outcome: WebhookDeliveryOutcome
    response_status: number | null
    duration_ms: number
    error: string | null
    request: {
        headers: Record<string, string>
        body: string
    }
    response_body: string | null
    attempted_at: string
}

export interface User {
    id: number
    anonymous_id: string
    external_id: string
    full_name?: string
    email?: string
    phone?: string
    timezone?: string
    locale?: string
    data: Record<string, any>
    devices?: Device[]
    created_at?: Date
    preferences_url?: string
}

export type UserUpdateParams = Partial<Pick<User, 'email' | 'phone' | 'timezone' | 'locale' | 'data'>>

export interface Device {
    device_id: string
    token?: string
    os: string
    model: string
    app_build: string
    app_version: string
}

export interface UserEvent {
    id: number
    uuid?: string
    name: string
    data: Record<string, any>
    created_at: string
}

export type ListState = 'draft' | 'ready' | 'loading'
type ListType = 'static' | 'dynamic' | 'synced'

export type ListSource = 'default' | 'mixpanel'

export interface ListSourceConfig {
    provider_id?: number
    cohort_id?: string
    cohort_name?: string
}

export type List = {
    id: number
    projectId: number
    name: string
    description?: string
    state: ListState
    type: ListType
    rule?: WrapperRule
    users_count: number
    tags?: string[]
    progress?: {
        complete: number
        total: number
    }
    is_visible: boolean
    folder_id?: number
    source?: ListSource
    source_config?: ListSourceConfig
    updated_by?: AdminLastUpdate
    created_at: string
    updated_at: string
} & (
    | {
        type: 'dynamic'
        rule: WrapperRule
    }
    | { type: 'static' }
    | { type: 'synced' }
)

export type DynamicList = List & { type: 'dynamic' }

export type ListCreateParams = Pick<List, 'name' | 'description' | 'rule' | 'type' | 'tags' | 'is_visible' | 'folder_id'>
export type ListUpdateParams = Pick<List, 'name' | 'description' | 'rule' | 'tags' | 'folder_id'>

export type UserUploadState = 'pending' | 'processing' | 'complete' | 'failed'

export interface UserUpload {
    id: number
    project_id: number
    list_id?: number
    filename: string
    original_name?: string
    state: UserUploadState
    headers?: string[]
    column_mapping?: Record<string, string>
    row_count: number
    processed_count: number
    error?: string
    created_at: string
    updated_at: string
}

export type ExportState = 'pending' | 'processing' | 'complete' | 'failed' | 'expired'
export type ExportType = 'list' | 'campaign'

export interface Export {
    id: number
    project_id: number
    admin_id?: number
    type: ExportType
    entity_id: number
    name?: string
    filter?: { states?: CampaignSendState[] }
    state: ExportState
    file_size?: number
    row_count: number
    processed_count: number
    error?: string
    expires_at?: string
    created_at: string
    updated_at: string
}

export type MailchimpImportState = 'processing' | 'complete' | 'failed'

export interface MailchimpAudience {
    id: string
    name: string
    member_count: number
    unsubscribe_count: number
    cleaned_count: number
}

export interface MailchimpTag {
    id: number
    name: string
    member_count: number
}

export interface MailchimpTagSelection {
    id: number
    name: string
}

export interface MailchimpCreatedList {
    id: number
    name: string
}

export interface MailchimpImport {
    id: string
    project_id: number
    audience_id: string
    audience_name?: string
    list_id?: number
    tags?: MailchimpTagSelection[]
    created_lists?: MailchimpCreatedList[]
    state: MailchimpImportState
    row_count?: number | null
    processed_count: number
    skipped_count: number
    error?: string
    created_at: string
    updated_at: string
}

export interface MailchimpImportParams {
    api_key: string
    audience_id: string
    list_id?: number
    tags?: MailchimpTagSelection[]
}

type JourneyStatus = 'draft' | 'live' | 'off'

export interface Journey {
    id: number
    parent_id?: number
    draft_id?: number
    name: string
    description?: string
    status: JourneyStatus
    tags?: string[]
    created_at: string
    updated_at: string
    deleted_at?: string
    stats_at?: string
    stats: Record<string, number>
    folder_id?: number
}

export interface JourneyStep<T = any> {
    id: number
    type: string
    name: string
    data: T
    x: number
    y: number
}

export type JourneyStepParams = Omit<JourneyStep, 'id'>

interface JourneyStepMapChild<E = any> {
    external_id: string
    path?: string
    data?: E
}

export interface JourneyStepMap {
    [external_id: string]: {
        type: string
        name: string
        data_key?: string
        data?: Record<string, unknown>
        x: number
        y: number
        children?: JourneyStepMapChild[]
        stats?: Record<string, number>
        stats_at?: Date
        id?: number
    }
}

export interface JourneyStepTypeEditProps<T> extends ControlledProps<T> {
    journey: Journey
    project: Project
    stepId?: number // if already saved
}

export interface JourneyStepTypeEdgeProps<T, E> extends ControlledProps<E> {
    stepData: T
    siblingData: E[] // does not include self
    journey: Journey
    project: Project
}

export interface JourneyStepType<T = any, E = any> {
    name: string
    icon: ReactNode
    category: 'entrance' | 'delay' | 'flow' | 'action' | 'exit' | 'info'
    description: string
    Describe?: ComponentType<JourneyStepTypeEditProps<T>>
    newData?: () => Promise<T>
    newEdgeData?: () => Promise<E>
    Edit?: ComponentType<JourneyStepTypeEditProps<T> & { nodes: Node[], disabled?: boolean }>
    EditEdge?: ComponentType<JourneyStepTypeEdgeProps<T, E>>
    sources?: string[]
    multiChildSources?: boolean
    hasDataKey?: boolean
    hideTopHandle?: boolean
    hideBottomHandle?: boolean
    maxWidth?: number | 'none'
}

export interface JourneyUserStep {
    id: number
    entrance_id: number
    type: string
    delay_until?: string
    created_at: string
    updated_at: string
    ended_at?: string

    user?: User
    journey?: Journey
    step?: JourneyStep
}

export interface StepError {
    message: string
    user_id: number
    created_at: string
}

export interface JourneyEntranceDetail {
    journey: Journey
    user: User
    userSteps: JourneyUserStep[]
}

export type CampaignState = 'draft' | 'loading' | 'scheduled' | 'running' | 'finished' | 'aborting' | 'aborted' | 'archived'

export interface CampaignDelivery {
    sent: number
    pending: number
    total: number
    opens: number
    clicks: number
    unsubscribes: number
}

export type CampaignType = 'blast' | 'trigger'

export type CampaignQuietHoursAction = 'skip' | 'delay'

export interface CampaignQuietHours {
    start: string // "HH:mm"
    end: string // "HH:mm"
    action: CampaignQuietHoursAction
}

export interface CampaignSettings {
    link_wrap: boolean
    priority: 'low' | 'none' | 'high'
    quiet_hours_enabled?: boolean
    quiet_hours?: CampaignQuietHours
    in_app_delivery?: InAppDeliveryMode
}

export type InAppDeliveryMode = 'broadcast' | 'individual'

export interface Campaign {
    id: number
    project_id: number
    type: CampaignType
    name: string
    channel: ChannelType
    state: CampaignState
    delivery: CampaignDelivery
    provider_id?: number
    provider?: Provider
    subscription_id?: number
    subscription?: Subscription
    templates: Template[]
    list_ids?: number[]
    lists?: List[]
    exclusion_list_ids?: number[]
    exclusion_lists?: List[]
    tags?: string[]
    journeys?: Journey[]
    send_in_user_timezone?: boolean
    send_at?: string
    ends_at?: string | null
    screenshot_url: string
    folder_id?: number
    progress?: {
        complete: number
        total: number
    }
    settings?: CampaignSettings
    created_at: string
    updated_at: string
    updated_by?: AdminLastUpdate
    deleted_at: string
}

export type CampaignSendState = 'pending' | 'sent' | 'throttled' | 'failed' | 'bounced' | 'aborted'

export type CampaignUpdateParams = Partial<Pick<Campaign, 'name' | 'state' | 'list_ids' | 'exclusion_list_ids' | 'subscription_id' | 'tags' | 'folder_id' | 'settings' | 'ends_at'>>
export type CampaignCreateParams = Pick<Campaign, 'name' | 'type' | 'list_ids' | 'exclusion_list_ids' | 'channel' | 'subscription_id' | 'provider_id' | 'tags' | 'folder_id' | 'settings'>
export type CampaignLaunchType = 'now' | 'later'
export type CampaignLaunchParams = Pick<Campaign, 'send_at' | 'send_in_user_timezone' | 'state' | 'ends_at'> & { launch_type?: CampaignLaunchType }
// export type ListUpdateParams = Pick<List, 'name' | 'rule'>
export type CampaignUser = User & { state: CampaignSendState, send_at: string }

interface TemplateField {
    name: string
    key: string
    defaultValue?: string
    type?: string
}

interface NamedEmail { name: string, address: string }
export interface EmailTemplateData {
    from: NamedEmail
    cc?: string
    bcc?: string
    reply_to?: string
    subject: string
    preheader?: string
    editor: 'code' | 'visual'
    text: string
    html: string
    mjml: string
}

// RCS Types
export type RCSMessageMode = 'text' | 'rich_card'

export interface RCSSuggestedReply {
    type: 'reply'
    text: string
    postback_data: string
}

export interface RCSUrlAction {
    type: 'url'
    text: string
    url: string
    postback_data?: string
}

export interface RCSDialAction {
    type: 'dial'
    text: string
    phone_number: string
    postback_data?: string
}

export interface RCSLocationAction {
    type: 'location'
    text: string
    latitude: number
    longitude: number
    label?: string
    postback_data?: string
}

export type RCSSuggestion = RCSSuggestedReply | RCSUrlAction | RCSDialAction | RCSLocationAction

export interface RCSCardMedia {
    file_url: string
    content_type?: string
    height: 'SHORT' | 'MEDIUM' | 'TALL'
}

export interface RCSCardContent {
    title?: string
    description?: string
    media?: RCSCardMedia
    suggestions?: RCSSuggestion[]
}

export interface RCSRichCard {
    card_orientation: 'VERTICAL' | 'HORIZONTAL'
    thumbnail_image_alignment?: 'LEFT' | 'RIGHT'
    card_content: RCSCardContent
}

export interface RCSContent {
    mode: RCSMessageMode
    rich_card?: RCSRichCard
    suggestions?: RCSSuggestion[]
}

export interface TextTemplateData {
    from: string
    text: string
    editor?: 'simple' | 'interactive'
    media_url?: string
    rcs?: RCSContent
}

export interface PushTemplateData {
    title: string
    body: string
    url: string
    custom: Record<string, unknown>
    badge?: number | string
    sound?: string
    category?: string
    collapse_key?: string
    thread_id?: string
    priority?: 'high' | 'normal'
    android_channel_id?: string
    tag?: string
}

export interface WebhookTemplateData {
    method: 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT'
    endpoint: string
    body: Record<string, any>
    headers: Record<string, string>
    cache_key?: string
}

type NotificationType = 'alert' | 'html'

export interface InAppTemplateData {
    title: string
    body: string
    layout?: number
    custom: Record<string, string | number>
    type: NotificationType
    html: string
    read_on_show?: boolean
    expires_at?: string
    expires_in_user_timezone?: boolean
    fields: TemplateField[]
    editor?: 'code' | 'popup'
    puck_data?: Record<string, any>
}

export type Template = {
    id: number
    campaign_id: number
    name?: string
    type: ChannelType
    locale: string
    data: any
    translation_status?: 'pending' | 'ready' | 'error'
    subscription?: Subscription
    created_at: string
    updated_at: string
    deleted_at?: string | null
} & (
    | {
        type: 'email'
        data: EmailTemplateData
    }
    | {
        type: 'text'
        data: TextTemplateData
    }
    | {
        type: 'push'
        data: PushTemplateData
    }
    | {
        type: 'webhook'
        data: WebhookTemplateData
    }
    | {
        type: 'in_app'
        data: InAppTemplateData
    }
)

export type TemplateCreateParams = Pick<Template, 'name' | 'type' | 'data' | 'campaign_id' | 'locale'> & {
    ai_translate?: boolean
}
export type TemplateUpdateParams = Pick<Template, 'name' | 'data'>
export type VariantUpdateParams = Pick<Template, 'name'> & { id?: number }

export interface TemplatePreviewParams {
    user?: Record<string, any>
    journey?: Record<string, any>
    context?: Record<string, any>
}

export interface TemplateProofParams {
    variables: TemplatePreviewParams
    recipient: string
}

export enum SubscriptionState {
    unsubscribed = 0,
    subscribed = 1,
    optedIn = 2,
}

export interface UserSubscription {
    id: number
    name: string
    channel: ChannelType
    subscription_id: number
    state: SubscriptionState
    created_at: string
    updated_at: string
}

export interface SubscriptionParams {
    state: SubscriptionState
    subscription_id: number
}

export interface Subscription {
    id: number
    name: string
    channel: ChannelType
    is_public: boolean
    is_opt_in: boolean
    is_channel: boolean
    created_at: string
    updated_at: string
}
export type SubscriptionCreateParams = Pick<Subscription, 'name' | 'channel' | 'is_public' | 'is_opt_in'>
export type SubscriptionUpdateParams = Pick<SubscriptionCreateParams, 'name'> & Partial<Pick<SubscriptionCreateParams, 'is_public'>>

export type ProviderGroup = 'email' | 'text' | 'push' | 'webhook' | 'ai'
export interface Provider {
    id: number
    name: string
    type: string
    group: string
    data: any
    rate_limit: number
    rate_interval: string
    setup: ProviderSetupMeta[]
    features?: { rcs: boolean, mms: boolean }
    domain_status?: 'not_configured' | 'pending' | 'verified'
    messaging_status?: MessagingStatus
}

export type EmailProvider = Provider & { group: 'email' } & { allowed_sending_domains?: string }

export interface SESDnsRecord {
    type: 'CNAME' | 'MX' | 'TXT'
    name: string
    value: string
    purpose: 'dkim' | 'mail_from_mx' | 'mail_from_spf' | 'dmarc'
    required: boolean
    status?: string
}

export interface SESDomainStatus {
    configured: boolean
    name?: string
    dkimTokens?: string[]
    mailFromSubdomain?: string
    region?: string
    dkimStatus?: string
    mailFromStatus?: string
    verifiedForSending?: boolean
    records?: SESDnsRecord[]
}

export type ProviderCreateParams = Pick<Provider, 'name' | 'data' | 'type' | 'group'> & Partial<Pick<Provider, 'rate_limit' | 'rate_interval'>>

export type MessagingStatus =
    | 'not_configured'
    | 'brand_pending'
    | 'brand_approved'
    | 'campaign_pending'
    | 'campaign_failed'
    | 'campaign_approved'
    | 'number_pending'
    | 'number_failed'
    | 'active'

export interface TelnyxMessagingStatus {
    status: MessagingStatus
    brand?: {
        brandId: string
        displayName: string
        brandStatus: string
    }
    vetting?: {
        vettingId: string
        vettingStatus: string
        vettingScore?: number
        vettingClass?: string
    }
    campaign?: {
        campaignId: string
        useCase: string
        campaignStatus: string
        description?: string
        sampleMessage1?: string
        sampleMessage2?: string
        sampleMessage3?: string
        messageFlow?: string
        helpMessage?: string
        optinMessage?: string
        optoutMessage?: string
        optinKeywords?: string
        optoutKeywords?: string
        helpKeywords?: string
        privacyPolicyLink?: string
        termsAndConditionsLink?: string
        embeddedLink?: boolean
        embeddedPhone?: boolean
        ageGated?: boolean
        subUsecases?: string[]
        failureReasons?: string
    }
    numberAssignment?: {
        phoneNumber: string
        assignmentStatus: string
        failureReasons?: string
    }
}

export interface TelnyxSubUsecase {
    id: string
    displayName: string
    description?: string
    classification?: string
    validSubUsecase?: boolean
}

export interface TelnyxAvailableNumber {
    phoneNumber: string
    vanityFormat?: string
    monthlyCost?: string
    upfrontCost?: string
    currency?: string
    region?: string
    features: string[]
    reservable: boolean
}

export interface TelnyxNumberSearchFilters {
    country_code?: string
    national_destination_code?: string
    contains?: string
    starts_with?: string
    limit?: number
}

export type ProviderUpdateParams = ProviderCreateParams
export interface ProviderMeta {
    name: string
    description?: string
    url?: string
    icon?: string
    type: string
    group: string
    schema: any
    paths?: Record<string, string>
    oauth?: boolean
}

export interface ProviderSetupMeta {
    name: string
    value: string
}

export interface Image {
    id: number
    uuid: string
    url: string
    thumbnail_url: string | null
    name: string
    original_name: string
    extension: string
    alt: string
    filesize: string
    status: 'pending' | 'processing' | 'ready' | 'error'
}

export interface GlobalDataSummary {
    key: string
    created_at: string
    updated_at: string
}

export interface GlobalData extends GlobalDataSummary {
    data: unknown
}

export type ResourceType = 'font' | 'snippet' | 'layout'
export interface Resource {
    id: number
    type: ResourceType
    name: string
    value: Record<string, any>
    prerender?: string
}

export interface Font {
    name: string
    url: string
    value: string
    weights?: number[]
}

export interface Tag {
    id: number
    name: string
    count?: number
}

export interface QueueMetric {
    data: Metric[]
    waiting: number
    providers?: Record<string, QueueMetric>
}

export interface Metric {
    date: string | Date | number
    count: number
}

export interface LocaleOption {
    key: string
    label: string
    shortLabel?: string
}

export interface Locale extends LocaleOption {
    id: number
}

export interface Series {
    label: string
    data: Metric[]
}

export interface Notification {
    id: number
    project_id: number
    user_id: number
    campaign_id?: number
    content_type: NotificationType
    content: any
    read_at?: string
    expires_at?: string
    created_at: string
    updated_at: string
}

export interface Audit {
    project_id: number
    admin_id?: number
    admin_email?: string
    admin_first_name?: string
    admin_last_name?: string
    event: string
    object: Record<string, any>
    object_changes: Record<string, any>
    item_id: number
    item_type: string
    created_at: string
}

export interface AdminLastUpdate extends Admin {
    created_at: string
}

export type LaunchApprovalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired'
export type LaunchApprovalEntityType = 'campaign' | 'journey'

export interface LaunchApproval {
    id: number
    project_id: number
    requester_id: number
    approver_id?: number
    entity_type: LaunchApprovalEntityType
    entity_id: number
    status: LaunchApprovalStatus
    launch_params?: Record<string, any>
    request_note?: string
    response_note?: string
    expires_at?: string
    responded_at?: string
    created_at: string
    updated_at: string

    // Populated by API
    requester?: { id: number, email: string, first_name?: string, last_name?: string }
    approver?: { id: number, email: string, first_name?: string, last_name?: string }
    entity_name?: string
}

export interface LaunchApprovalCreateParams {
    entity_type: LaunchApprovalEntityType
    entity_id: number
    launch_params?: Record<string, any>
    request_note?: string
    approver_id?: number
}

export interface LaunchApprovalResponseParams {
    response_note?: string
}

export interface CampaignApprovalRequestParams {
    state: 'scheduled'
    send_in_user_timezone?: boolean
    send_at?: string
    ends_at?: string | null
    request_note?: string
    approver_id?: number
}

export interface UsageChannelBreakdown {
    email: number
    push: number
    text: number
    webhook: number
    total: number
}

export interface MonthlyUsage extends UsageChannelBreakdown {
    month: string
}

export interface YearlyUsage extends UsageChannelBreakdown {
    year: number
}

export interface ProjectUsage {
    year: number
    yearly: YearlyUsage
    monthly: MonthlyUsage[]
    years: YearlyUsage[]
}
